import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Chip,
  Divider,
  ExpandMoreRoundedIcon,
  Flex,
  Rotate,
  Text,
} from '@shared/components';
import { useDisclosure } from '@shared/hooks';
import { formatDate, type PropsWithChildren } from '@shared/utils';

export interface BlockedRecordAccordionProps extends PropsWithChildren {
  label: string;
  title: string;
  blockedUntil: Date | null;
  examCenterCode: string | null;
  defaultExpanded?: boolean;
  endAdornment?: JSX.Element;
}

export default function BlockedRecordAccordion(props: Readonly<BlockedRecordAccordionProps>) {
  const { label, title, blockedUntil, examCenterCode, children, defaultExpanded, endAdornment } =
    props;
  const [isOpen, { toggle }] = useDisclosure(defaultExpanded);

  return (
    <Accordion
      disableGutters
      elevation={0}
      expanded={isOpen}
      onChange={toggle}
      sx={(theme) => ({
        border: '1px solid',
        borderColor: theme.palette.grey[300],
        overflow: 'hidden',
        '&.MuiAccordion-root': {
          borderRadius: theme.shape.rounded(2),
        },
      })}
    >
      <AccordionSummary
        component={Flex}
        expandIcon={
          <Rotate in={false}>
            <ExpandMoreRoundedIcon />
          </Rotate>
        }
        sx={(theme) => ({
          p: theme.spacing(4),
          gap: theme.spacing(4),
          '& .MuiAccordionSummary-content': { m: theme.spacing(0), alignItems: 'center' },
        })}
      >
        <Flex sx={(theme) => ({ gap: theme.spacing(4), width: '100%', mr: theme.spacing(3) })}>
          <Flex sx={(theme) => ({ flexDirection: 'column', gap: theme.spacing(1), flex: 1 })}>
            <Text variant='body2' sx={(theme) => ({ color: theme.palette.text.secondary })}>
              {label}:
            </Text>
            <Text variant='body1Semibold'>{title}</Text>
          </Flex>
          <Flex sx={(theme) => ({ flexDirection: 'column', gap: theme.spacing(1) })}>
            <Text variant='body2' sx={(theme) => ({ color: theme.palette.text.secondary })}>
              ถูกระงับสิทธิ์ถึง:
            </Text>
            <Text variant='body1Semibold'>{blockedUntil ? formatDate(blockedUntil) : '-'}</Text>
          </Flex>
          <Divider orientation='vertical' flexItem />
          <Flex
            sx={(theme) => ({
              flexDirection: 'column',
              gap: theme.spacing(1),
              alignItems: 'flex-start',
            })}
          >
            <Text variant='body2' sx={(theme) => ({ color: theme.palette.text.secondary })}>
              ศูนย์ทดสอบ:
            </Text>
            {examCenterCode ? (
              <Chip size='small' label={examCenterCode} sx={{ fontWeight: 600 }} />
            ) : (
              <Text variant='body1Semibold'>-</Text>
            )}
          </Flex>
        </Flex>
        {endAdornment}
      </AccordionSummary>
      <Divider
        sx={(theme) => ({
          borderColor: theme.palette.grey[300],
        })}
      />
      <AccordionDetails
        sx={(theme) => ({
          p: theme.spacing(0),
        })}
      >
        {children}
      </AccordionDetails>
    </Accordion>
  );
}
